import { useModal } from '../../contexts/ModalContext';
import { useBreakpoint } from '../../hooks/use-breakpoint';
import { createModalHistoryId, registerModalHistory, unregisterModalHistory } from './modal-history';
import { ModalProps } from './modal-prop.interface';
import classNames from 'classnames';
import { ChevronLeft, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getCarouselNavButtonClassName,
  getCarouselNavIconClassName,
} from 'src/components/CarouselNavButtons/carousel-nav-button.styles';

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  modalBodyClassName,
  hideCloseButton = false,
  fullScreen = false,
  showBackButton = false,
  onBack = () => {},
  overlayClassName,
  isBackButtonDisabled = false,
  closeOnBackdropClick = true,
}) => {
  const { closeModal, openModal, isOpen: isGlobalModalOpen } = useModal();
  const { isMobile } = useBreakpoint();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [modalHistoryId] = useState(createModalHistoryId);
  const onCloseRef = useRef(onClose);
  const closeGlobalModalRef = useRef(closeModal);

  onCloseRef.current = onClose;
  closeGlobalModalRef.current = closeModal;

  useEffect(() => {
    if (!isOpen) {
      unregisterModalHistory(modalHistoryId);
      return;
    }

    registerModalHistory(modalHistoryId, () => {
      void onCloseRef.current();
      closeGlobalModalRef.current();
    });

    return () => unregisterModalHistory(modalHistoryId);
  }, [isOpen, modalHistoryId]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsExiting(false);
      // Small delay to ensure DOM is ready for animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
      openModal();
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      if (isGlobalModalOpen) {
        closeModal();
      }

      if (isMobile) {
        setIsExiting(true);
        setIsAnimating(false);
        const exitTimer = setTimeout(() => {
          setIsVisible(false);
          setIsExiting(false);
        }, 300); // Match CSS exit duration
        return () => clearTimeout(exitTimer);
      } else {
        setIsAnimating(false);
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 250); // Faster timing for snappier feel
        return () => clearTimeout(timer);
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [closeModal, isGlobalModalOpen, isOpen, isMobile, openModal]);

  const handleClose = () => {
    void onClose?.();
    closeModal();
  };

  if (!isVisible && !isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className={classNames(overlayClassName, 'fixed inset-0 z-[120] transition-all duration-350 ease-out', {
        'opacity-0 pointer-events-none': !isAnimating,
        'opacity-100': isAnimating,
        'modal-overlay-backdrop': !fullScreen,
        'modal-overlay-backdrop-fullscreen': fullScreen,
      })}
      onMouseDown={(e) => {
        if (closeOnBackdropClick && e.target === e.currentTarget) {
          e.currentTarget.dataset.shouldClose = 'true';
        }
      }}
      onMouseUp={(e) => {
        if (closeOnBackdropClick && e.currentTarget.dataset.shouldClose === 'true' && e.target === e.currentTarget) {
          void handleClose();
        }
        e.currentTarget.dataset.shouldClose = 'false';
        e.stopPropagation();
      }}
    >
      <div
        className={classNames(
          className,
          // Updated: cleaner, minimal modal - single color, subtle shadow, no accent borders
          'bg-white shadow-xl relative z-50 transition-all duration-250 ease-out transform',
          isMobile ? 'modal-mobile-optimized modal-touch-friendly' : '',
          'rounded-xl flex flex-col',
          {
            'max-h-[90vh] max-w-[720px] overflow-hidden': !fullScreen,
            'w-[100dvw] h-[100dvh] overflow-none': fullScreen,
            // Mobile-optimized animation with smoother transforms
            'opacity-0 scale-98 translate-y-5': !isAnimating && !isMobile,
            'opacity-100 scale-100 translate-y-0': isAnimating && !isMobile,
            // Mobile uses CSS classes for better performance
            'modal-entered': isAnimating && isMobile,
            'modal-exiting': isExiting && isMobile,
          }
        )}
        onClick={(e) => e.stopPropagation()}
        onMouseUp={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className={classNames(
            modalBodyClassName,
            'relative flex flex-col items-center transition-all duration-350 ease-out',
            {
              'px-4 pt-4 pb-4 md:max-w-full md:p-6 md:mx-0 flex-1 min-h-0 overflow-hidden': !fullScreen,
              'w-full h-full p-6 overflow-none': fullScreen,
            }
          )}
        >
          {showBackButton && (
            <div
              className={classNames('absolute top-4 z-50', {
                'md:left-4 left-[-0.5rem]': !fullScreen,
                'left-4': fullScreen,
              })}
            >
              <button
                type="button"
                aria-label="Back"
                disabled={isBackButtonDisabled}
                className={getCarouselNavButtonClassName({ variant: 'floating', disabled: isBackButtonDisabled })}
                onClick={onBack}
              >
                <ChevronLeft className={getCarouselNavIconClassName('floating')} />
              </button>
            </div>
          )}
          {!hideCloseButton && (
            <div
              className={classNames('absolute top-4 z-50', {
                'md:right-4 right-4': !fullScreen,
                'right-4': fullScreen,
              })}
            >
              <button
                type="button"
                aria-label="Close"
                className={getCarouselNavButtonClassName({ variant: 'floating' })}
                onClick={() => void handleClose()}
              >
                <X className={getCarouselNavIconClassName('floating')} />
              </button>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};

export default Modal;
